import { useRef, useEffect, useCallback } from 'react';
import { REMOTE_MESSAGE_ACTIONS } from '../../calling-remote/constants/remoteMessageActions';
import { getDndMessageHandler, useDndMessageHandler } from '../../common/hooks/useDndMessageHandler';
import { componentIds } from '../../common/constants/DndComponent';
const FRAME_PADDING = 16;
function getWidgetBoundary(componentName) {
  const widgetIframe = window.document.getElementById(componentIds[componentName]);
  if (!widgetIframe) {
    return {};
  }
  const widgetRect = widgetIframe.getBoundingClientRect();
  return {
    widgetIframe,
    widgetRect
  };
}

/**
 * @param {ComponentName} - This is used to validate the message's origin component
 */
export const useDndWidget = componentName => {
  const handleMessageRef = useRef(() => void 0);
  const removeEventListeners = () => {
    window.removeEventListener('message', handleMessageRef.current);
    window.removeEventListener('mouseup', removeEventListeners);
  };
  function startDragging({
    baseMouseX,
    baseMouseY
  }) {
    if (Number.isNaN(+baseMouseX) || Number.isNaN(+baseMouseY)) {
      return;
    }
    const {
      widgetIframe,
      widgetRect
    } = getWidgetBoundary(componentName);
    if (!widgetIframe) {
      return;
    }
    let frameTop = widgetRect.top;
    let frameLeft = widgetRect.left;
    const clientHeight = document.documentElement.clientHeight;
    const clientWidth = document.documentElement.clientWidth;
    function handleFrameMousemove({
      offsetX,
      offsetY
    }) {
      const {
        widgetIframe: currentWidget,
        widgetRect: currentRect
      } = getWidgetBoundary(componentName);
      if (!currentWidget) {
        return;
      }
      const nextFrameTop = frameTop + offsetY;
      const nextFrameLeft = frameLeft + offsetX;
      frameLeft = Math.max(nextFrameLeft, FRAME_PADDING); // limit left
      frameTop = Math.max(nextFrameTop, FRAME_PADDING); // limit top

      if (frameLeft + currentRect.width > clientWidth // limit right
      ) {
        frameLeft = clientWidth - currentRect.width - FRAME_PADDING;
      }
      if (nextFrameTop + currentRect.height > clientHeight // limit bottom
      ) {
        frameTop = clientHeight - currentRect.height - FRAME_PADDING;
      }
      currentWidget.style.left = `${frameLeft}px`;
      currentWidget.style.top = `${frameTop}px`;
    }
    handleMessageRef.current = getDndMessageHandler((action, payload) => {
      if (action === REMOTE_MESSAGE_ACTIONS.MOVING_WIDGET) {
        handleFrameMousemove(payload);
      }
      if (action === REMOTE_MESSAGE_ACTIONS.STOP_DRAGGING) {
        removeEventListeners();
      }
    }, componentName);
    window.addEventListener('message', handleMessageRef.current);
    window.addEventListener('mouseup', removeEventListeners);
  }
  const handleResize = useCallback(() => {
    const {
      widgetIframe,
      widgetRect
    } = getWidgetBoundary(componentName);
    if (!widgetIframe || !widgetRect.width) {
      return;
    }
    let frameTop = widgetRect.top;
    let frameLeft = widgetRect.left;
    const clientHeight = document.documentElement.clientHeight;
    const clientWidth = document.documentElement.clientWidth;
    if (frameLeft + FRAME_PADDING + widgetRect.width > clientWidth) {
      frameLeft = Math.max(clientWidth - widgetRect.width - FRAME_PADDING, FRAME_PADDING);
    }
    if (frameTop + FRAME_PADDING + widgetRect.height > clientHeight) {
      frameTop = Math.max(clientHeight - widgetRect.height - FRAME_PADDING, FRAME_PADDING);
    }
    if (frameLeft !== widgetRect.left) {
      widgetIframe.style.left = `${frameLeft}px`;
    }
    if (frameTop !== widgetRect.top) {
      widgetIframe.style.top = `${frameTop}px`;
    }
  }, [componentName]);
  useDndMessageHandler((action, payload) => {
    if (action === REMOTE_MESSAGE_ACTIONS.START_DRAGGING) {
      startDragging(payload);
    }
  }, componentName);
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);
};